import { useState } from 'react';

import { fetchPostDeReferralsValidate } from '@iwoca/lapi-client/edge_de';
import { Input } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import { TGetStartedDataDE } from './GetStartedDE';
import styles from './GetStartedDE.module.css';
import {
  getReferralSession,
  removeReferralSession,
  saveReferralSession,
} from '../../../helpers/referralSession';
import ButtonLink from '../../ButtonLink/ButtonLink';

type TValidationStatus = 'idle' | 'confirmed' | 'invalid';

type TProps = {
  onValidatingChange: (isValidating: boolean) => void;
};

const ReferralCodeFieldDE = ({ onValidatingChange }: TProps) => {
  const [showReferralField, setShowReferralField] = useState(false);
  const [validationStatus, setValidationStatus] =
    useState<TValidationStatus>('idle');
  const [hasStoredSession] = useState(() => Boolean(getReferralSession()));

  const { t } = useLingui();
  const { register, getValues } = useFormContext<TGetStartedDataDE>();

  const onBlur = async () => {
    const code = getValues('referralCode')?.trim();
    if (!code) return;
    try {
      onValidatingChange(true);
      const result = await fetchPostDeReferralsValidate({
        body: { referral_code: code, source_type: 'code' },
      });
      if (result.valid) {
        saveReferralSession(code, result);
        setValidationStatus('confirmed');
      } else {
        setValidationStatus('invalid');
      }
    } catch {
      // silently ignore — referral must never break signup
    } finally {
      onValidatingChange(false);
    }
  };

  if (hasStoredSession) return null;

  return (
    <div>
      {!showReferralField && (
        <ButtonLink
          className={styles.referralToggle}
          onClick={() => setShowReferralField(true)}
          data-testid="referral-code-toggle"
        >
          <Trans>I have a referral code</Trans>
        </ButtonLink>
      )}
      {showReferralField && (
        <Input
          {...register('referralCode', {
            onChange: () => {
              setValidationStatus('idle');
              removeReferralSession();
            },
          })}
          onBlur={onBlur}
          label={t`Referral code`}
          placeholder={t`Enter your referral code`}
          data-testid="referral-code-input"
          success={
            validationStatus === 'confirmed'
              ? t`Referral code confirmed`
              : undefined
          }
          error={
            validationStatus === 'invalid'
              ? t`This referral code is not valid`
              : undefined
          }
        />
      )}
    </div>
  );
};

export default ReferralCodeFieldDE;
